import React from 'react';
import { useTranslation } from 'react-i18next';

const EventExplanation = {
  VOICECB: 'Voice Webhook Callback from Twilio - details in the "message" column',
  VIDEOCB: 'Video Webhook Callback from Twilio - details in the "message" column',
  USRPOPUP: 'Patient is active in the HBSUK Platform, but not in the session page',
  RESTRICTED: '',
  USRWAIT: 'The Patient is on the session page waiting for the clinician to call/the clinician is in the appointment',
  CAUDIOSTART: 'The clinician has started ringing the patient for a voice call',
  CACALLING: 'The clinician is calling the patient for a video call',
  USRDECLINED: 'The patient declined the video call',
  CADECLINED: 'The clinician cancelled the video call ringer (before the call starts)',
  RINGER_DISPLAYED: 'The patient has received the clinicians request to start a video call and displayed the ringer',
  START: 'The patient has accepted, and therefore started, the call',
  CASTART: 'The clinician acknowledges that the patient has accepted the call and starts their video',
  USRLOG: 'Logs information about the call (see message column)',
  RECEIVING_VIDEO: 'The user is receiving a video stream from the other party',
  VIDEO_ID: 'The Twilio Video Room ID',
  CAEND: 'The clinician has ended a video call',
  CLINICIAN_CALL_FAILED: 'An Twilio error has occurred. Misnomer, the call itself may not have failed'
};

// prettier-ignore
const tryFormatJSON = (message) => {
  if (!message) return message;

  try {
    return JSON.stringify(JSON.parse(message), null, 2);
  } catch {//NOSONAR
    return message.toString();
  }
};

const CellStyle = {
  border: '1px solid lightgrey',
  padding: 8,
  overflow: 'hidden',
  minWidth: 0
};

const HeaderCellStyle = {
  ...CellStyle,
  fontWeight: 600
};

const columns =
  'minmax(0, max-content) minmax(0, max-content) minmax(0, max-content) minmax(0, 10fr) minmax(0, max-content) minmax(0, max-content)';

type TwilioEvent = {
  created_date: Date;
  user_type_code: string;
  event: string;
  message: string;
  detail1: string;
  detail2: string;
};

// Component to display a table of Twilio events (just the created_date, user_type_code, event, message, detail1 and detail2 columns)
export function TwilioEventsTable({ events }: { events: TwilioEvent[] }) {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        border: '1px solid lightgrey'
      }}
    >
      {/* Header */}
      <div style={{ display: 'contents' }}>
        <div style={HeaderCellStyle}>{t('created_date')}</div>
        <div style={HeaderCellStyle}>{t('user_type_code')}</div>
        <div style={HeaderCellStyle}>{t('event')}</div>
        <div style={HeaderCellStyle}>{t('message')}</div>
        <div style={HeaderCellStyle}>{t('detail1')}</div>
        <div style={HeaderCellStyle}>{t('detail2')}</div>
      </div>

      {/* Body */}
      {events.map((twilioEvent) => (
        <div key={twilioEvent.created_date.toString()} style={{ display: 'contents' }}>
          <div style={CellStyle}>
            <span title={twilioEvent.created_date.toString()}>
              {twilioEvent.created_date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          </div>
          <div style={CellStyle}>{twilioEvent.user_type_code}</div>
          <div style={CellStyle} title={EventExplanation[twilioEvent.event]}>
            {twilioEvent.event}
          </div>
          <div style={CellStyle}>
            {/* Allow the JSON in this scroll to scroll independently */}
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre', // keep indentation/newlines
                overflowX: 'auto', // horizontal scroll for long paths
                maxWidth: '100%', // stay inside the cell
                display: 'block'
              }}
            >
              {tryFormatJSON(twilioEvent.message)}
            </pre>
          </div>
          <div style={CellStyle}>{twilioEvent.detail1}</div>
          <div style={CellStyle}>{twilioEvent.detail2}</div>
        </div>
      ))}
    </div>
  );
}
